import {
  WhmAlert,
  WhmAlertTitle,
  WhmBox,
  WhmTypography,
} from "@securitize/reactjs-whm";
import useTranslations from "../../hooks/useTranslations";
import useSeasonBannerQuery from "../../data/useSeasonBannerQuery";

/**
 * Tax Season expectations banner (TA-4716).
 *
 * The gateway resolves the whole decision — season window plus whether this
 * investor is on the audience list Ops uploaded — and hands back two i18n keys
 * plus the dates the copy quotes. Ops retimes the banner from the feature flag
 * and rewords it from the Control Panel, neither of which needs a release.
 */

/**
 * Renders "YYYY-MM-DD" in the investor's language. Forced to UTC: the gateway
 * sends a bare calendar day, and letting the browser localise it would shift
 * "2027-01-31" to the 30th for anyone west of UTC.
 */
function formatDate(value: string, language: string): string {
  const parsed = new Date(`${value}T00:00:00Z`);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  try {
    return new Intl.DateTimeFormat(language, {
      timeZone: "UTC",
      month: "long",
      day: "numeric",
    }).format(parsed);
  } catch {
    // An unexpected language tag must not blank out the date.
    return value;
  }
}

function SeasonBanner() {
  const { getTranslation, language } = useTranslations();
  const { banner } = useSeasonBannerQuery();

  if (!banner.show || !banner.messageKey) {
    return null;
  }

  const replacements = Object.entries(banner.dates ?? {}).reduce<
    Record<string, string>
  >(
    (acc, [name, value]) => ({ ...acc, [name]: formatDate(value, language) }),
    {},
  );

  const message = getTranslation(banner.messageKey, replacements);
  const title = banner.titleKey
    ? getTranslation(banner.titleKey, replacements)
    : undefined;

  // getTranslation echoes the key back when it is missing from i18n. Showing
  // "tax-center.season-banner.message" to an investor is worse than showing
  // nothing, so a key the flag names but i18n does not have renders no banner.
  if (message === banner.messageKey) {
    return null;
  }

  // Same reasoning for a placeholder the flag never defined: copy that reads
  // "expected by {divDate}" is worse than no banner at all.
  if (/\{[^}]+\}/.test(message)) {
    return null;
  }

  return (
    <WhmAlert
      severity="warning"
      variant="standard"
      data-test-id="tax-season-banner"
      sx={{ marginBottom: "16px" }}
    >
      <WhmBox sx={{ flexGrow: 1 }}>
        {title && title !== banner.titleKey && (
          <WhmAlertTitle>{title}</WhmAlertTitle>
        )}
        <WhmTypography variant="body2">{message}</WhmTypography>
      </WhmBox>
    </WhmAlert>
  );
}

export default SeasonBanner;
